import { useState } from 'react';
import { toast } from 'sonner';
import { ShieldIcon, Trash2Icon, UserCheckIcon, UserXIcon } from 'lucide-react';
import useDocumentTitle from '@/hooks/useDocumentTitle';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  ADMIN_ROLE,
  SUPERADMIN_ROLE,
  useAdminUsers,
  useDeleteUser,
  useIsAdmin,
  useSetUserEnabled,
  useSetUserRoles,
} from '@/hooks/useAdmin';
import { AdminUser } from '@/schema/admin';
import { Column, DataTable } from '../components/DataTable';
import { SectionCard } from '../components/SectionCard';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { EM_DASH, formatDate } from '../lib/format';

const PAGE_SIZE = 15;

export function AdminPeople() {
  useDocumentTitle('Admin - People');
  const [page, setPage] = useState(0);
  const [pendingDelete, setPendingDelete] = useState<AdminUser | null>(null);
  const { data, isLoading } = useAdminUsers(page, PAGE_SIZE);
  const { profile, isSuperAdmin } = useIsAdmin();
  const setEnabled = useSetUserEnabled();
  const setRoles = useSetUserRoles();
  const deleteUser = useDeleteUser();

  const onError = (error: unknown) => {
    toast.error(error instanceof Error ? error.message : 'The change was rejected');
  };

  const roleBadgeClass = (role: string) => {
    if (role === SUPERADMIN_ROLE) return 'border-amber-400/45 bg-amber-400/15 text-amber-200';
    if (role === ADMIN_ROLE) return 'border-primary/45 bg-primary/15 text-primary-200';
    return 'border-white/15 bg-white/5 text-white/60';
  };

  const columns: Column<AdminUser>[] = [
    {
      key: 'user',
      header: 'User',
      render: (row) => (
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-white">
            {row.orthodontistName ?? row.username}
          </p>
          <p className="truncate text-xs text-white/45">{row.email}</p>
        </div>
      ),
    },
    {
      key: 'clinic',
      header: 'Clinic',
      render: (row) => <span className="text-sm text-white/80">{row.clinicName ?? EM_DASH}</span>,
    },
    {
      key: 'roles',
      header: 'Roles',
      render: (row) => (
        <div className="flex flex-wrap gap-1">
          {row.roles.map((role) => (
            <span
              key={role}
              className={cn(
                'inline-flex items-center rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.18em]',
                roleBadgeClass(role),
              )}
            >
              {role}
            </span>
          ))}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <span className={cn('text-sm', row.enabled ? 'text-emerald-300' : 'text-white/45')}>
          {row.enabled ? 'Active' : 'Disabled'}
        </span>
      ),
    },
    {
      key: 'created',
      header: 'Joined',
      render: (row) => <span className="text-sm text-white/70">{formatDate(row.createdAt)}</span>,
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      // Account administration is the superadmin's; an admin only reads this list.
      render: (row) => {
        if (!isSuperAdmin) return null;

        // The backend refuses all of these on your own account; hiding them avoids
        // offering an action that can only fail.
        const isSelf = row.id === profile?.id;
        const isAdminUser = row.roles.includes(ADMIN_ROLE);
        const isSuperAdminUser = row.roles.includes(SUPERADMIN_ROLE);

        return (
          <div className="flex items-center justify-end gap-1">
            <Button
              type="button"
              variant="ghost-light"
              size="sm"
              disabled={isSelf || setRoles.isPending}
              title={isAdminUser ? 'Remove admin role' : 'Grant admin role'}
              onClick={() =>
                setRoles.mutate(
                  {
                    id: row.id,
                    roles: isAdminUser
                      ? row.roles.filter((role) => role !== ADMIN_ROLE)
                      : [...row.roles, ADMIN_ROLE],
                  },
                  { onError },
                )
              }
            >
              <ShieldIcon className={cn(isAdminUser && 'text-primary-300')} />
              Admin
            </Button>
            <Button
              type="button"
              variant="ghost-light"
              size="sm"
              disabled={isSelf || setRoles.isPending}
              title={isSuperAdminUser ? 'Remove superadmin role' : 'Grant superadmin role'}
              onClick={() =>
                setRoles.mutate(
                  {
                    id: row.id,
                    roles: isSuperAdminUser
                      ? row.roles.filter((role) => role !== SUPERADMIN_ROLE)
                      // Both, so the account still reads as an admin wherever only that is checked.
                      : [...new Set([...row.roles, ADMIN_ROLE, SUPERADMIN_ROLE])],
                  },
                  { onError },
                )
              }
            >
              <ShieldIcon className={cn(isSuperAdminUser && 'text-amber-300')} />
              Super
            </Button>
            <Button
              type="button"
              variant={row.enabled ? 'ghost-danger' : 'ghost-light'}
              size="sm"
              disabled={isSelf || setEnabled.isPending}
              title={row.enabled ? 'Disable sign-in' : 'Re-enable sign-in'}
              onClick={() => setEnabled.mutate({ id: row.id, enabled: !row.enabled }, { onError })}
            >
              {row.enabled ? <UserXIcon /> : <UserCheckIcon />}
            </Button>
            <Button
              type="button"
              variant="ghost-danger"
              size="sm"
              disabled={isSelf}
              title="Delete this account"
              onClick={() => setPendingDelete(row)}
            >
              <Trash2Icon />
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <SectionCard
      title="People"
      description={data ? `${data.totalElements} accounts` : undefined}
    >
      <DataTable
        columns={columns}
        rows={data?.content ?? []}
        rowKey={(row) => row.id}
        page={page}
        totalPages={data?.totalPages ?? 0}
        onPageChange={setPage}
        isLoading={isLoading}
        emptyMessage="No accounts yet."
      />

      {pendingDelete && (
        <ConfirmDialog
          title="Delete account"
          confirmLabel="Delete account"
          isPending={deleteUser.isPending}
          error={deleteUser.error?.message}
          onClose={() => setPendingDelete(null)}
          onConfirm={() =>
            deleteUser.mutate(pendingDelete.id, {
              onSuccess: () => {
                setPendingDelete(null);
                toast.success('Account deleted');
              },
            })
          }
        >
          <p>
            <strong>{pendingDelete.email}</strong> is removed for good. Their audit entries,
            issue reports and invitations stay, they just stop pointing at an account.
          </p>
          <p className="text-white/50">
            Disabling sign-in is the reversible option if you only want to lock them out.
          </p>
        </ConfirmDialog>
      )}
    </SectionCard>
  );
}

export default AdminPeople;
